import { Component, OnInit, OnDestroy } from '@angular/core';
import { Util } from 'src/shared/util/util';
import { ModalController } from '@ionic/angular';
import { ActivatedRoute } from '@angular/router';

import { BookJob } from 'src/providers/book-job/book-job';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Homekeeper } from 'src/providers/homekeeper/homekeeper';
import { Invoices } from 'src/providers/invoices/invoices';
import { Logger } from 'src/providers/logger';
import { Proposals } from 'src/providers/proposals/proposals';
import { Pros } from 'src/providers/pros/pros';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Loading } from 'src/shared/components/loading/loading';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { ProposalModel } from 'src/shared/models/proposals.model';
import { deepClone } from 'src/tidy-ui-components/utils/deep-clone';
import { Communication } from 'src/providers/communication/communication';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Schedule } from 'src/providers/schedule/schedule';
import { DateTime as LuxonDateTime } from 'luxon';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';

import { PendingProposalPage } from 'src/pages/proposals/pending-proposal/pending-proposal';
import { PaidInvoicePage } from 'src/pages/invoices/paid-invoice/paid-invoice';
import { PendingInvoicePage } from 'src/pages/invoices/pending-invoice/pending-invoice';
import { MyProsEditProPage } from 'src/pages/my-pros/edit-pro/edit-pro';
import { PastJobPage } from 'src/pages/more/past-job/past-job';
import { PastPrivateJobPage } from 'src/pages/more/past-private-job/past-private-job';
import { Subscription } from 'rxjs';
import { MessageThreadPage } from 'src/pages/messages/message-thread/message-thread';
import { MyProSettingsPage } from 'src/pages/my-pro-settings/my-pro-settings';
import { ProPropertiesPage } from 'src/pages/my-pros/my-pro/pro-properties/pro-properties';
import { getMyProDropDownMenu } from 'src/pages/my-pros/my-pro/my-pro-dropdown-menu';
import { BookService } from 'src/shared/services/book.service';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'my-pro.html',
    standalone: false
})

export class MyProPage implements OnInit, OnDestroy {

  addresses: any;
  assignedProperties: any[] = [];
  assignedPropertiesCount = 0;
  assignedPropertiesSentence = '';
  assignedPropertiesLabel = '';
  acceptedProposals: ProposalModel[] = [];
  address: any;
  available: boolean;
  allowsInvoiceAutopay: boolean;
  autoPayMaxAmount: number;
  creditBalance: any;
  customBackPage: string;
  pendingProposals: ProposalModel[] = [];
  pendingInvoices = [];
  pro: any;
  pastJobs: any;
  requestable = true;
  proId;
  prosData;
  privatePlanId: number;
  isCurrentProBlocked = false;
  errorMessage: string;
  userRole: string | null;
  form: UntypedFormGroup;
  messages: any;
  submitted: boolean;
  serviceCategories: any;
  isRentalClient: boolean;
  isProLoaded: boolean;
  isPastJobsLoaded: boolean;
  isProsDataLoaded: boolean;
  isBillingsDataLoaded: boolean;
  isMessagesLoaded: boolean;
  dialogParams: any;
  isRightSideContent = true;
  loaded: boolean;
  blacklistedPro: any;
  maxJobSettings: any;
  proDropdownMenu: { label: string; action: string }[] = [];
  private dropDownMenuSubscription: Subscription;
  private isDestroyed = false;

  constructor(
    private bookJobService: BookJob,
    private client: Client,
    private homekeeper: Homekeeper,
    private invoices: Invoices,
    private navCtrl: CustomNavController,
    private proposals: Proposals,
    private pros: Pros,
    private modalController: ModalController,
    private route: ActivatedRoute,
    private logger: Logger,
    private storage: TidyStorage,
    public communication: Communication,
    private fb: UntypedFormBuilder,
    private schedule: Schedule,
    private util: Util,
    private rightSidePanelService: RightSidePanelService,
    private windowService: WindowService,
    private bookService: BookService
  ) {
    this.form = this.fb.group({
      message: ['', Validators.required],
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    await this.loadData();
    if (this.pro) {
      this.dropDownMenuSubscription = this.rightSidePanelService.dropDownMenuAction$.subscribe((action) => {
        if (action === 'goToProSettings') {
          this.goToProSettings();
        } else if (action === 'blockPro') {
          this.blockProConfirmation();
        }
      });
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.dropDownMenuSubscription) {
      this.dropDownMenuSubscription.unsubscribe();
    }
  }

  // Called once the blocked state is known (loadProsData), so a blocked pro never gets the menu.
  private updateDropdownMenu(): void {
    // A late response from a pro the user already left must not overwrite the next pro's panel menu.
    if (this.isDestroyed) {
      return;
    }
    this.proDropdownMenu = this.pro ? getMyProDropDownMenu(this.pro.is_private, this.isCurrentProBlocked) : [];
    this.rightSidePanelService.currentComponentData = { dropDownMenu: this.proDropdownMenu.length ? this.proDropdownMenu : null };
  }

  goToProSettings() {
    const params = {
      pro: this.pro,
      maxJobSettings: this.maxJobSettings
    }
    this.rightSidePanelService.dropDownMenuAction$.next(null);
    this.rightSidePanelService.navigateTo('my-pro-settings', params, MyProSettingsPage);
  }

  async loadData() {
    this.rightSidePanelService.setDialogLoading(true);
    this.pendingInvoices = [];
    this.loaded = false;
    this.userRole = localStorage.getItem('user_role');
    this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.customBackPage = localStorage.getItem('myProBackPage') || (this.isRentalClient && this.windowService.isDesktopRes ? 'contacts' : 'job-request-workflows');
    this.serviceCategories = await this.pros.getServiceCategories();
    await this.loadProDetails();
    this.loadPastJobs();
    this.loadProsData();
    this.loadBillingsData();
    this.loadMessages();
    this.autopayConfig();
    this.loaded = true;
    this.rightSidePanelService.setDialogLoading(false);
    this.rightSidePanelService.setDialogPageTitle(this.pro?.homekeeper?.name);
  }

  async loadProDetails(): Promise<void> {
    try {
      this.isProLoaded = false;
      this.proId = parseInt(this.route.snapshot.paramMap.get('proId')) || this.dialogParams?.proId;
      const addressId = await this.resolveAddressId(this.proId);
      this.pro = await this.pros.getProDetail(this.proId, addressId);
      this.rightSidePanelService.setDialogPageTitle(this.pro?.homekeeper?.name);
      this.available = this.pro?.availability?.includes('available to serve' );
      await this.loadAssignedProperties();
      this.isProLoaded = true;
    } catch (error) {
      this.isProLoaded = true;
      this.errorHandler(error);
    }
  }

  // Resolve an address this pro actually serves so the address-scoped pro-detail
  // endpoint gets a valid id. Mirrors my-pros.goToPro: prefer the currently
  // known/selected address if the pro serves it, otherwise fall back to the
  // pro's first served address. Needed when my-pro is opened without an address
  // in params (e.g. "View Contact" from Messages), where localStorage['addressId']
  // may be empty or point to an address this pro doesn't serve.
  private async resolveAddressId(proId: number): Promise<number | null> {
    const paramAddressId = Number(this.dialogParams?.address?.id);
    const selectedAddressId = parseInt(localStorage.getItem('addressId'));
    const known = !isNaN(paramAddressId) ? paramAddressId : selectedAddressId;

    const allPros = await this.pros.getAllPros();
    const currentPro = allPros.find((pro) => pro.id === Number(proId));
    const priorities = currentPro?.address_homekeeper_priorities ?? [];
    if (!priorities.length) {
      return isNaN(known) ? null : known;
    }

    const servesKnown = !isNaN(known) && priorities.some((priority) => priority.address_id === known);
    const resolvedId = servesKnown ? known : priorities[0].address_id;
    localStorage.setItem('addressId', String(resolvedId));
    return resolvedId;
  }

  async loadPastJobs(): Promise<void> {
    try {
      this.isPastJobsLoaded = false;
      this.pastJobs = await this.homekeeper.getHkHistory(this.proId);
      this.isPastJobsLoaded = true;
    } catch (error) {
      this.isPastJobsLoaded = true;
      this.errorHandler(error);
    }
  }

  async loadAssignedProperties(): Promise<void> {
    const allPros = await this.pros.getAllPros();
    const currentPro = allPros.find(p => p.id === parseInt(this.proId));
    if (currentPro?.address_homekeeper_priorities) {
      this.addresses = await this.client.getMoreDetailAddresses();
      this.assignedProperties = [];
      currentPro.address_homekeeper_priorities.forEach(priority => {
        const address = this.addresses.find(addr => addr.id === priority.address_id);
        if (address) {
          const serviceCategory = this.serviceCategories.find(cat => cat.id === priority.service_type_id);
          const serviceName = serviceCategory?.name === 'Regular Cleaning' ? 'Cleaning' : (serviceCategory?.name || 'Unknown');
          this.assignedProperties.push({
            id: address.id,
            name: address.address_name || address.address1,
            color: address.color || '#1E2125',
            serviceTypeId: priority.service_type_id,
            serviceName,
            serviceIcon: serviceCategory?.icon_url || 'assets/img/mop.png'
          });
        }
      });
      this.assignedPropertiesCount = new Set(this.assignedProperties.map(p => p.id)).size;
      this.assignedPropertiesSentence = this.getAssignedPropertiesSentence();
      this.assignedPropertiesLabel = this.getAssignedPropertiesLabel();
    }
  }

  getAssignedPropertiesLabel() {
    const t = (key: string) => new TranslationPipe().transform(key);
    return `${t('Assigned to')} ${this.assignedPropertiesCount} ${this.assignedPropertiesCount === 1 ? t('Property') : t('Properties')}`;
  }

  getAssignedPropertiesSentence() {
    let sentence = '';
    this.assignedProperties.map((address) => {
      if (!sentence.includes(address?.name)) {
        sentence += `${address?.name  }, `;
      }
    });
    return sentence.slice(0, -2);
  }

  checkNotEmpty(array) {
    if (!array || array.length === 0) {
      return false;
    }

    // Check if all jobs have required properties that are not null
    return array.every(job => job &&
             job.cleaning_id !== null &&
             job.service !== null &&
             job.start_datetime_local !== null &&
             job.display_address !== null &&
             job.is_private !== null);
  }

  async loadProsData(): Promise<void> {
    try {
      this.isProsDataLoaded = false;
      const serviceTypeId = 1;
      this.prosData = await this.pros.getPros(serviceTypeId);
      const blacklistedPros = await this.pros.getBlacklistedPros();
      this.blacklistedPro = blacklistedPros.find(pro => pro.homekeeper.id === this.proId);
      this.isCurrentProBlocked = this.blacklistedPro ? true : false;
      this.updateDropdownMenu();
      this.isProsDataLoaded = true;
    } catch (error) {
      this.isProsDataLoaded = true;
      this.errorHandler(error);
    }
  }

  async loadBillingsData(): Promise<void> {
    try {
      this.isBillingsDataLoaded = false;
      this.address = await this.getAddress();
      if (this.pro?.is_private) {
        this.creditBalance = await this.pros.getCreditBalance(this.pro.team.id, this.address.id);
        this.pendingInvoices = await this.invoices.getInvoices({
          teamId: this.pro.team.id,
          status: 'pending',
          jobId: null
        });
        const proposals = await this.proposals.getProposalsByTeam(this.pro.team.id);
        this.parseProposals(proposals);
      }
      this.isBillingsDataLoaded = true;
    } catch (error) {
      this.isBillingsDataLoaded = true;
      this.errorHandler(error);
    }
  }

  async loadMessages(): Promise<void> {
    try {
      if (!this.pro?.team?.id) {
        this.isMessagesLoaded = true;
        return;
      }
      const messages = await this.communication.getSharedInbox(this.pro.team.id);
      this.messages = messages.body;
    } catch (error) {
      this.errorHandler(error);
    }
    this.isMessagesLoaded = true;
  }

  goToMessageThreadPage() {
    const params = {
      backPage: 'messages',
      proId: this.proId,
      proDetail: this.pro
    };
    this.rightSidePanelService.navigateTo('message-thread', params, MessageThreadPage);
  }

  errorHandler(error: any): void {
    const errorMessage = (error.error && error.error.message) ? error.error.message : error.message || error;
    this.util.showError(errorMessage);
  }

  async autopayConfig() {
    if (!this.pro?.team?.id) {
      return;
    }
    const autopaySettings = await this.pros.getAutopaySettings(this.pro.team.id);
    autopaySettings.map((settings) =>{
      if (settings?.key === 'max_scheduled_jobs_per_day') {
        this.maxJobSettings = {
          maxJobs: settings?.value,
          limitMaxJobs: settings?.value && settings?.value > -1 ? true : false
        }
      }
      if (settings?.key === 'payment.autopay_limit') {
        this.autoPayMaxAmount = (parseInt(settings?.value) / 100)
      } else if (settings?.key === 'payment.autopay') {
        this.allowsInvoiceAutopay = (settings?.value === "true");
      }
    });
  }

  parseProposals(proposals) {
    this.pendingProposals = [];
    this.acceptedProposals = [];
    proposals.map(proposal => {
      if (proposal.status === 'pending') {
        this.pendingProposals.push(proposal);
      }
    });
    proposals.map(proposal => {
      if (proposal.status === 'accepted') {
        this.acceptedProposals.push(proposal);
      }
    });
  }

  async goToBookJob() {
    this.bookJobService.clearBookJobStorage();
    await this.storage.save('bookJobBackPage', 'contacts');
    // Booking can land on three different pages depending on account type and the
    // booking-view experiment, and they read the Pro from different params:
    // SelectCategoryPage (rental clients) reads selectedPro, BookJobPage reads
    // hkId + filterByPro. Send both so the Pro survives whichever view resolves.
    const params = {
      address: this.address,
      bookingType: 'add_job',
      selectedPro: this.pro,
      hkId: this.proId,
      filterByPro: true,
      categoryId: this.navCtrl.getParam('categoryId') || this.dialogParams?.categoryId
    };
    await this.bookService.goToBookJob(params);
  }

  contactPro() {
    return window.location.href = `tel:${this.pro?.homekeeper?.phone}`;
  }

  async favoriteProConfirmation() {
    const t = (key: string) => new TranslationPipe().transform(key);
    const params: ConfirmPageParamsModel = {
      title: `${t('Favorite')} ${this.pro.homekeeper.first_name}?`,
      body: t('This will move them to the top of your favorites list.'),
      backText: t('Go Back'),
      confirmText: t('Confirm Favorite'),
      confirmAction: this.moveProToCollection.bind(this, this.prosData.favorited)
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async blockProConfirmation() {
    const t = (key: string) => new TranslationPipe().transform(key);
    const params: ConfirmPageParamsModel = {
      title: `${t('Block')} ${this.pro.homekeeper.first_name}?`,
      body: t('This will block them from your account, so they cannot see or accept any of your future job requests.'),
      backText: t('Go Back'),
      confirmText: t('Confirm Block'),
      confirmAction: this.blacklistPro.bind(this)
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async blacklistPro() {
    try {
      await this.pros.blacklistPro([this.pro.homekeeper.id]);
      // Cache invalidation now handled by Pros service via TanStack Query
      // Refresh blocked state so the menu and Approve & Unblock follow if this page stays open.
      this.loadProsData();
      const url = this.isRentalClient && this.windowService.isDesktopRes ? 'contacts' : 'job-request-workflows';
      this.navCtrl.navigateRoot(url);
      this.modalController.dismiss();
      this.util.showSuccess(new TranslationPipe().transform('Pro blocked successfully'));
    } catch (error) {
      this.errorHandler(error);
      this.errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
      this.util.showError(`${new TranslationPipe().transform('Error blocking pro:')} ${this.errorMessage}`);
      throw error;
    }
  }

  async unblacklistPro() {
    try {
      await this.pros.unblacklistPro(this.blacklistedPro.id);
      // Cache invalidation now handled by Pros service via TanStack Query
      // Refresh blocked state so the menu and Approve & Unblock follow if this page stays open.
      this.loadProsData();
      const url = this.isRentalClient && this.windowService.isDesktopRes ? 'contacts' : 'job-request-workflows';
      this.navCtrl.navigateRoot(url);
      this.modalController.dismiss();
      this.util.showSuccess(new TranslationPipe().transform('Pro unblocked successfully'));
    } catch (error) {
      this.errorHandler(error);
      this.errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
      this.util.showError(`${new TranslationPipe().transform('Error unblocking pro:')} ${this.errorMessage}`);
      throw error;
    }
  }

  async approveAndUnblockProConfirmation() {
    const t = (key: string) => new TranslationPipe().transform(key);
    const params: ConfirmPageParamsModel = {
      title: `${t('Approve & Unblock')} ${this.pro.homekeeper.first_name}`,
      body: t('This will give your approval to them to see and accept future job requests.'),
      backText: t('Go Back'),
      confirmText: `${t('Approve & Unblock')} ${this.pro.homekeeper.first_name}`,
      confirmAction: this.unblacklistPro.bind(this)
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async moveProToCollection(collectionToAdd) {
    const approvedIndex = this.prosData.approved.findIndex(pro => pro.object.id === this.proId);
    const favoritedIndex = this.prosData.favorited.findIndex(pro => pro.object.id === this.proId);
    const blockedIndex = this.prosData.blocked.findIndex(pro => pro.object.id === this.proId);

    let removedElement: any[];

    if (approvedIndex >= 0) {
      removedElement = this.prosData.approved.splice(approvedIndex, 1);
    }

    if (favoritedIndex >= 0) {
      removedElement = this.prosData.favorited.splice(favoritedIndex, 1);
    }

    if (blockedIndex >= 0) {
      removedElement = this.prosData.blocked.splice(blockedIndex, 1);
    }

    if (removedElement) {
      const indexToAdd = 0;
      collectionToAdd.splice(indexToAdd, 0, ...removedElement);
    }

    const serviceTypeId = 1;
    try {
      await this.pros.updateProsPriorities(this.prosData, serviceTypeId);
      // Cache invalidation now handled by Pros service via TanStack Query
      const url = this.isRentalClient && this.windowService.isDesktopRes ? 'contacts' : 'job-request-workflows';
      this.navCtrl.navigateRoot(url);
    } catch (err) {
      this.logger.error(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    } finally {
      this.modalController.dismiss();
    }
  }

  goToProposalPage(proposal) {
    this.storage.save('proposalUuId', proposal.uuid);
    this.rightSidePanelService.navigateTo(`proposal/${proposal.uuid}`, {}, PendingProposalPage);
  }

  async goToInvoicePage(invoice) {
    try {
      const settings = await this.client.getClientSettings();
      const params = {
        invoice,
        invoiceId: invoice.id,
        hasAccount: true,
        proName: this.pro.homekeeper.first_name,
        proEmail: this.pro.homekeeper.email,
        proPhone: this.pro.homekeeper.phone,
        proTeamId: this.pro.team.id,
        proId: this.pro.homekeeper.id,
        clientName: settings.profile.first_name,
        clientEmail: settings.profile.email,
        successRoute: `my-pro/${this.proId}`
      }
      const path = invoice.full_paid ? `paid-bill/${invoice.id}` : `pending-bill/${invoice.id}`;
      const component = invoice.full_paid ? PaidInvoicePage : PendingInvoicePage;
      this.rightSidePanelService.navigateTo(path, params, component);
    } catch (err) {
      this.logger.error(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  goToBillsPage() {
    this.navCtrl.navigateRoot('bills');
  }

  checkIfHasPlanWithPro() {
    let hasPlan = false;
    if (this.address?.plans?.length) {
      if (this?.pro?.is_private) {
        this.address?.plans?.map((plan) => {
          if (plan?.is_private && plan?.team?.owner?.id === this.proId) {
            hasPlan = true;
            this.privatePlanId = plan?.id;
          }
        });
      } else {
        this.address?.plans?.map((plan) => {
          if (!plan?.is_private) {
            hasPlan = true;
          }
        });
      }
    }
    return hasPlan;
  }

  async getAddress() {
    const addressId = parseInt(localStorage.getItem('addressId'));
    const addressResponse = await this.client.getMoreDetailAddresses();
    return addressResponse.find(ad => ad.id === addressId);
  }

  async goToSendPayment() {
    const params = {
      pro: this.pro,
      allowsInvoiceAutopay: this.allowsInvoiceAutopay,
      privatePlanId: this.privatePlanId,
      address: this.address,
      pendingInvoices: this.pendingInvoices
    }
    if (this.pendingInvoices.length) {
      this.rightSidePanelService.navigateTo('send-payment-or-pay-invoice', params);
    } else {
      this.rightSidePanelService.navigateTo('send-payment', params);
    }
  }

  goToProBillingHistoryPage() {
    const params = {
      billingData: this.creditBalance,
      proId: this.pro.homekeeper.id,
      proTeamId: this.pro.team.id,
      proName: `${this.pro.homekeeper.first_name  } ${  this.pro.homekeeper.last_name}`,
      cameFromMyProPage: true
    };
    this.rightSidePanelService.navigateTo('billing-history', params);
  }

  goToEditContactInfo() {
    const pro = deepClone(this.pro.homekeeper);
    this.rightSidePanelService.navigateTo(`edit-pro/${pro.id}`, {pro}, MyProsEditProPage);
  }

  goToPastJob(jobId, isPrivate) {
    const url = isPrivate ? `past-private-job/${jobId}` : `past-job/${jobId}`;
    const component = isPrivate ? PastPrivateJobPage : PastJobPage;
    localStorage.setItem('pastJobBackPage', `my-pro/${this.proId}`);
    this.rightSidePanelService.navigateTo(url, {jobId, backPage: `my-pro/${this.proId}`, proId: this.proId}, component);
  }

  goToConciergePage() {
    const params = {
      title: 'Turn On Auto Assign',
      type: 'support.other',
      question: `Please turn on auto assign for my private pro ${  this.pro.homekeeper.first_name  } (ID ${  this.pro.homekeeper.id  }).`
    }
    this.rightSidePanelService.navigateTo('contact-concierge', params);
  }

  @Loading('', true)
  async sendMessage(attachments = []) {
    this.errorMessage = '';
    this.submitted = true;
    if (attachments.length === 0 && !this.form.get('message').valid) {
      return;
    }
    const payload = {
      chat: {
        type: 'account',
        data: {
          team_id: this.pro.team.id
        }
      },
      message: {
        text: this.form.value.message,
        files: attachments
      }
    };
    try {
      const response = await this.communication.sendSharedInboxMessage(payload);
      if (response?.data?.message === 'We couldn\'t send your message, please try again later!') {
        return this.errorMessage = response.data.message;
      }
      this.form.patchValue({
        message: ''
      });
      const messages = await this.communication.getSharedInbox(this.pro.team.id);
      this.messages = messages.body.reverse();
      this.messages = this.messages.slice(0, 2);
      this.submitted = false;
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  getDate(date) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return LuxonDateTime.fromISO(date).setZone(timezone).toRelative();
  }

  async sendAttachment() {
    this.errorMessage = '';
    try {
      const attachment = await this.schedule.addAttachment();
      if (attachment.location === '') {
        return this.errorMessage = 'Unable to attach photo. Please upload a PNG or JPEG file.';
      }
      await this.sendMessage([attachment.location]);
    } catch (err) {
      this.errorMessage =  err.error ? err.error.message : err.message;
    }
  }

  async goToPropertiesList() {
    const params = {
      properties: this.assignedProperties,
      proName: this.pro?.homekeeper?.name || 'Pro',
      proId: this.proId,
      serviceCategories: this.serviceCategories
    };
    this.rightSidePanelService.navigateTo('pro-properties', params, ProPropertiesPage);
  }


}
